import React, { useEffect, useState } from "react";
import { Box, CircularProgress, Grid, MenuItem, TextField } from "@mui/material";
import { useHd } from "../HelpdeskContext";
import { hd, errorMessage } from "../api";
import { NumberField, SaveBar, Section, Toggle } from "./common";

/** Loads the settings document and lets a section edit a slice of it. */
export const useSettings = () => {
  const ctx = useHd();
  const [orig, setOrig] = useState(null);
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    hd.settings().then((x) => { setOrig(x); setS(x); }).catch((err) => ctx.notify(errorMessage(err), "error"));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const set = (section, key) => (v) => setS((p) => (section ? { ...p, [section]: { ...p[section], [key]: v } } : { ...p, [key]: v }));
  const save = async (sections) => {
    setSaving(true);
    try {
      const body = {};
      for (const sec of sections) body[sec] = sec === "root" ? undefined : s[sec];
      if (sections.includes("root")) Object.assign(body, { ticketPrefix: s.ticketPrefix, ticketNumberStart: s.ticketNumberStart });
      delete body.root;
      const next = await hd.updateSettings(body);
      setOrig({ ...s, ...next });
      setS({ ...s, ...next });
      ctx.notify("Settings saved");
      ctx.reload();
    } catch (err) {
      ctx.notify(errorMessage(err), "error");
    } finally {
      setSaving(false);
    }
  };
  return { s, set, save, saving, dirty: JSON.stringify(s) !== JSON.stringify(orig), reset: () => setS(orig) };
};

export default function GeneralSettings() {
  const { s, set, save, saving, dirty, reset } = useSettings();
  if (!s) return <CircularProgress size={22} />;
  const sections = ["root", "tickets", "notifications", "unknownSender", "csat", "email", "attachments"];
  return (
    <Box>
      <Section title="Ticket numbers">
        <Grid container spacing={2}>
          <Grid item><TextField size="small" label="Prefix" value={s.ticketPrefix} onChange={(e) => set(null, "ticketPrefix")(e.target.value.toUpperCase())} helperText="Tickets look like PREFIX-10291" inputProps={{ maxLength: 8 }} /></Grid>
          <Grid item><NumberField label="Numbering starts at" value={s.ticketNumberStart} onChange={set(null, "ticketNumberStart")} /></Grid>
        </Grid>
      </Section>
      <Section title="Resolution & reopening">
        <Toggle label="Resolution note is required" checked={s.tickets.resolutionNoteRequired} onChange={set("tickets", "resolutionNoteRequired")} />
        <Toggle label="Customer reply reopens a resolved/closed ticket" checked={s.tickets.reopenOnCustomerReply} onChange={set("tickets", "reopenOnCustomerReply")} />
        <Toggle label="Auto-assign new tickets within their team" checked={s.tickets.autoAssignOnCreate} onChange={set("tickets", "autoAssignOnCreate")} />
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item><NumberField label="Reopen window (days)" value={s.tickets.reopenWindowDays} onChange={set("tickets", "reopenWindowDays")} helperText="Later replies to closed tickets open a new ticket" /></Grid>
          <Grid item><NumberField label="Auto-close after resolved (hours)" value={s.tickets.autoCloseAfterHours} onChange={set("tickets", "autoCloseAfterHours")} helperText="0 disables auto-close" /></Grid>
          <Grid item>
            <TextField select size="small" label="On reopen, assign to" value={s.tickets.assignOnReopen} onChange={(e) => set("tickets", "assignOnReopen")(e.target.value)} sx={{ minWidth: 200 }}>
              <MenuItem value="previous">Previous agent</MenuItem>
              <MenuItem value="reassign">Re-assign in team</MenuItem>
              <MenuItem value="unassigned">Nobody (unassigned)</MenuItem>
            </TextField>
          </Grid>
        </Grid>
      </Section>
      <Section title="Customer notifications" description="Which emails the customer receives. Wording is edited in Email templates.">
        <Toggle label="Acknowledgement when a ticket is created by email" checked={s.notifications.sendAcknowledgement} onChange={set("notifications", "sendAcknowledgement")} />
        <Toggle label="Email agent replies to the customer" checked={s.notifications.sendAgentReplies} onChange={set("notifications", "sendAgentReplies")} />
        <Toggle label="Email when a ticket is resolved" checked={s.notifications.sendResolved} onChange={set("notifications", "sendResolved")} />
        <Toggle label="Email when a ticket is closed" checked={s.notifications.sendClosed} onChange={set("notifications", "sendClosed")} />
        <Toggle label="Notify agents when assigned" checked={s.notifications.notifyAgentOnAssign} onChange={set("notifications", "notifyAgentOnAssign")} />
        <Toggle label="Notify agents when the customer replies" checked={s.notifications.notifyAgentOnCustomerReply} onChange={set("notifications", "notifyAgentOnCustomerReply")} />
      </Section>
      <Section title="Unknown senders" description="What to create when an email or chat comes from someone not yet in the CRM.">
        <TextField select size="small" value={s.unknownSender.createAs} onChange={(e) => set("unknownSender", "createAs")(e.target.value)} sx={{ minWidth: 320 }}>
          <MenuItem value="contact">Contact (under "Individual Customers")</MenuItem>
          <MenuItem value="contact_with_company">Contact + Company from email domain</MenuItem>
          <MenuItem value="contact_and_lead">Contact + Company + Lead</MenuItem>
        </TextField>
        {s.unknownSender.createAs === "contact_and_lead" && (
          <TextField size="small" label="Lead pipeline" value={s.unknownSender.leadPipeline} onChange={(e) => set("unknownSender", "leadPipeline")(e.target.value)} sx={{ ml: 2 }} />
        )}
      </Section>
      <Section title="CSAT survey">
        <Toggle label="Ask for a rating after resolution" checked={s.csat.enabled} onChange={set("csat", "enabled")} />
        <NumberField label="Survey link valid for (days)" value={s.csat.expiresAfterDays} onChange={set("csat", "expiresAfterDays")} min={1} />
      </Section>
      <Section title="Email security">
        <Toggle label="Ignore auto-replies and out-of-office mail" checked={s.email.ignoreAutoReplies} onChange={set("email", "ignoreAutoReplies")} />
        <Toggle label="Reject mail that fails DMARC" checked={s.email.rejectDmarcFail} onChange={set("email", "rejectDmarcFail")} />
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item><NumberField label="Max new tickets per sender / hour" value={s.email.maxNewTicketsPerSenderPerHour} onChange={set("email", "maxNewTicketsPerSenderPerHour")} min={1} /></Grid>
          <Grid item xs={12} md={7}><TextField size="small" fullWidth label="Blocked senders (address or @domain, comma separated)" value={(s.email.blockedSenders || []).join(", ")} onChange={(e) => set("email", "blockedSenders")(e.target.value.split(",").map((x) => x.trim()).filter(Boolean))} /></Grid>
        </Grid>
      </Section>
      <Section title="Attachments">
        <Grid container spacing={2}>
          <Grid item><NumberField label="Max file size (MB)" value={s.attachments.maxFileSizeMb} onChange={set("attachments", "maxFileSizeMb")} min={1} max={25} /></Grid>
          <Grid item><NumberField label="Max files per message" value={s.attachments.maxFilesPerMessage} onChange={set("attachments", "maxFilesPerMessage")} min={1} max={10} /></Grid>
          <Grid item xs={12}><TextField size="small" fullWidth label="Allowed extensions" value={(s.attachments.allowedExtensions || []).join(", ")} onChange={(e) => set("attachments", "allowedExtensions")(e.target.value.split(",").map((x) => x.trim().toLowerCase()).filter(Boolean))} helperText="Executables, scripts and HTML are always blocked" /></Grid>
        </Grid>
      </Section>
      <SaveBar dirty={dirty} saving={saving} onSave={() => save(sections)} onReset={reset} />
    </Box>
  );
}
